import React, { useContext, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import moment from "moment";

import { AuthContext } from "../contexts/AuthContext";
import { ClientContext } from "../contexts/ClientContext";
import { ScreenContext } from "../contexts/ScreenContext";
import ConfirmDialog from "../components/ConfirmDialog";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import TablePagination from "@material-ui/core/TablePagination";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import LinearProgress from "@material-ui/core/LinearProgress";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Collapse from "@material-ui/core/Collapse";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import InputAdornment from "@material-ui/core/InputAdornment";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    overflowX: 'auto',
  },
  table: {
    '& .MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(0.75, 1.5),
      fontSize: '0.813rem',
      lineHeight: 1.2,
    },
    '& .MuiTableCell-root': {
      padding: theme.spacing(0.5, 1.5),
      fontSize: '0.813rem',
      lineHeight: 1.3,
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5, 1),
        fontSize: '0.75rem',
      },
    },
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
  dialogContent: {
    minWidth: 500,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      padding: theme.spacing(2, 1),
    },
  },
  contactCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  contactChip: {
    height: 18,
    fontSize: '0.65rem',
    marginLeft: theme.spacing(0.5),
  },
  contactsSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
  },
  contactCard: {
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  contactCardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  contactActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  actionButton: {
    minWidth: 44,
    minHeight: 44,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
  expandedRow: {
    backgroundColor: theme.palette.grey[50],
  },
  expandedContent: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
  },
  contactsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(0.75, 1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
  },
  expandButton: {
    padding: theme.spacing(0.5),
  },
}));

const ManageClients = () => {
  const classes = useStyles();
  const { user, token } = useContext(AuthContext);
  const { clients, loadClients } = useContext(ClientContext);
  const { setSnackbarMessage } = useContext(ScreenContext);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/";

  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [originalClientId, setOriginalClientId] = useState(null);
  const [idCheckMessage, setIdCheckMessage] = useState('');
  const [idAvailable, setIdAvailable] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('ID');
  const [order, setOrder] = useState('asc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [rowContacts, setRowContacts] = useState({});

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  
  // Additional contacts state
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [contactFormData, setContactFormData] = useState({
    Contact: '',
    MobilePhone: '',
    Email: ''
  });
  const [editingContactId, setEditingContactId] = useState(null);
  
  // Digital Ocean projects state
  const [doProjects, setDoProjects] = useState([]);
  const [doProjectsLoading, setDoProjectsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    ID: null,
    Name: '',
    Type: '',
    Address1: '',
    Address2: '',
    City: '',
    State: '',
    Zip: '',
    Phone: '',
    Contact: '',
    Email: '',
    membersince: ''
  });

  useEffect(() => {
    if (user.type === 'super_admin') {
      loadClients();
      loadDigitalOceanProjects();
    }
  }, []);

  // When opening dialog to add new client, suggest next ID
  useEffect(() => {
    if (dialogOpen && !editMode) {
      // Fetch the next available ID from the server
      axios.get(`${API_URL}get-next-client-id`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        setFormData(prev => ({
          ...prev,
          ID: response.data.nextId
        }));
      })
      .catch(error => {
        console.error('Error fetching next client ID:', error);
        // Fallback to client-side calculation if API fails
        if (clients.length > 0) {
          const maxId = Math.max(...clients.map(c => parseInt(c.ID) || 0));
          setFormData(prev => ({
            ...prev,
            ID: maxId + 1
          }));
        }
      });
    }
  }, [dialogOpen, editMode]);

  const fetchNextClientId = async () => {
    try {
      const response = await axios.get(`${API_URL}get-next-client-id`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData(prev => ({
        ...prev,
        ID: response.data.nextId
      }));
      
      // Trigger validation for the new ID
      checkClientIdAvailability(response.data.nextId);
    } catch (error) {
      console.error('Error fetching next client ID:', error);
      setSnackbarMessage('Error fetching next available ID');
    }
  };

  const handleOpenDialog = (clientToEdit = null) => {
    if (clientToEdit) {
      setEditMode(true);
      setOriginalClientId(clientToEdit.ID);
      setIdCheckMessage('');
      setIdAvailable(true);
      setFormData({
        ID: clientToEdit.ID,
        Name: clientToEdit.Name || '',
        Type: clientToEdit.Type || '',
        Address1: clientToEdit.Address1 || '',
        Address2: clientToEdit.Address2 || '',
        City: clientToEdit.City || '',
        State: clientToEdit.State || '',
        Zip: clientToEdit.Zip || '',
        Phone: clientToEdit.Phone || '',
        Contact: clientToEdit.Contact || '',
        Email: clientToEdit.Email || '',
        membersince: clientToEdit.membersince || ''
      });
      // Load additional contacts for this client
      loadAdditionalContacts(clientToEdit.ID);
    } else {
      setEditMode(false);
      setOriginalClientId(null);
      setIdCheckMessage('');
      setIdAvailable(true);
      setAdditionalContacts([]);
      setFormData({
        ID: null,
        Name: '',
        Type: '',
        Address1: '',
        Address2: '',
        City: '',
        State: '',
        Zip: '',
        Phone: '',
        Contact: '',
        Email: '',
        membersince: ''
      });
    }
    setContactFormData({ Contact: '', MobilePhone: '', Email: '' });
    setEditingContactId(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setOriginalClientId(null);
    setIdCheckMessage('');
    setIdAvailable(true);
    setFormData({
      ID: null,
      Name: '',
      Type: '',
      Address1: '',
      Address2: '',
      City: '',
      State: '',
      Zip: '',
      Phone: '',
      Contact: '',
      Email: '',
      membersince: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Check ID availability when ID field changes
    if (field === 'ID') {
      if (value) {
        checkClientIdAvailability(value);
      } else {
        setIdCheckMessage('Client ID cannot be blank');
        setIdAvailable(false);
      }
    }
  };

  const checkClientIdAvailability = async (clientId) => {
    if (!clientId) {
      setIdCheckMessage('Client ID cannot be blank');
      setIdAvailable(false);
      return;
    }

    // Check if ID is at least 3 digits (100 or greater)
    const idNumber = parseInt(clientId);
    if (isNaN(idNumber) || idNumber < 100) {
      setIdCheckMessage('Client ID must be at least a 3-digit number (100 or greater)');
      setIdAvailable(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}check-client-id`,
        { 
          clientId: idNumber,
          excludeId: editMode ? originalClientId : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIdAvailable(response.data.available);
      setIdCheckMessage(response.data.message);
    } catch (error) {
      console.error('Error checking client ID:', error);
      if (error.response?.data?.message) {
        setIdCheckMessage(error.response.data.message);
      } else {
        setIdCheckMessage('Error checking ID availability');
      }
      setIdAvailable(false);
    }
  };

  const handleSaveClient = async () => {
    // Validation
    if (!formData.Name) {
      setSnackbarMessage("Client name is required");
      return;
    }

    if (!formData.ID) {
      setSnackbarMessage("Client ID is required");
      return;
    }

    if (!idAvailable) {
      setSnackbarMessage("Client ID is already in use. Please choose a different ID.");
      return;
    }

    setIsLoading(true);

    try {
      const endpoint = editMode ? 'update-client' : 'create-client';

      await axios.post(
        API_URL + endpoint,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbarMessage(`Client ${editMode ? 'updated' : 'created'} successfully`);
      handleCloseDialog();
      loadClients(); // Reload client list
    } catch (error) {
      console.error("Save client error:", error);
      setSnackbarMessage(error.response?.data?.error || `Failed to ${editMode ? 'update' : 'create'} client`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClient = async (client) => {
    // First, check the impact of deleting this client
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        API_URL + 'check-client-deletion',
        { ID: client.ID },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setClientToDelete(client);
      setDeletionImpact(response.data.impact);
      setDeleteDialogOpen(true);
      setDeleteConfirmation('');
    } catch (error) {
      console.error("Check deletion error:", error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to check deletion impact');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    // Verify user typed the exact client name
    if (deleteConfirmation !== clientToDelete.Name) {
      setSnackbarMessage('Client name does not match. Deletion cancelled.');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(
        API_URL + 'delete-client',
        { 
          ID: clientToDelete.ID,
          clientName: clientToDelete.Name,
          confirmed: true
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbarMessage(`Client "${clientToDelete.Name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      setDeletionImpact(null);
      setDeleteConfirmation('');
      loadClients(); // Reload client list
    } catch (error) {
      console.error("Delete client error:", error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to delete client');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setClientToDelete(null);
    setDeletionImpact(null);
    setDeleteConfirmation('');
  };

  // Additional contacts management
  const loadAdditionalContacts = async (clientId) => {
    try {
      const response = await axios.get(
        `${API_URL}get-client-contacts/${clientId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAdditionalContacts(response.data.contacts || []);
    } catch (error) {
      console.error("Load contacts error:", error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to load contacts');
    }
  };

  const handleAddContact = async () => {
    if (!contactFormData.Contact) {
      setSnackbarMessage('Contact name is required');
      return;
    }

    if (!formData.ID) {
      setSnackbarMessage('Please save the client first before adding contacts');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}create-contact`,
        {
          ID: formData.ID,
          Contact: contactFormData.Contact,
          MobilePhone: contactFormData.MobilePhone,
          Email: contactFormData.Email
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbarMessage('Contact added successfully');
      setContactFormData({ Contact: '', MobilePhone: '', Email: '' });
      loadAdditionalContacts(formData.ID);
      loadClients(); // Refresh to update contact count badge
    } catch (error) {
      console.error("Add contact error:", error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to add contact');
    }
  };

  const handleEditContact = (contact) => {
    setEditingContactId(contact.pkid);
    setContactFormData({
      Contact: contact.Contact,
      MobilePhone: contact.MobilePhone || '',
      Email: contact.Email || ''
    });
  };

  const handleUpdateContact = async () => {
    if (!contactFormData.Contact) {
      setSnackbarMessage('Contact name is required');
      return;
    }

    try {
      await axios.post(
        `${API_URL}update-contact`,
        {
          pkid: editingContactId,
          Contact: contactFormData.Contact,
          MobilePhone: contactFormData.MobilePhone,
          Email: contactFormData.Email
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbarMessage('Contact updated successfully');
      setContactFormData({ Contact: '', MobilePhone: '', Email: '' });
      setEditingContactId(null);
      loadAdditionalContacts(formData.ID);
    } catch (error) {
      console.error("Update contact error:", error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to update contact');
    }
  };

  const handleDeleteContact = async (pkid) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Contact',
      message: 'Are you sure you want to delete this contact?',
      onConfirm: () => executeDeleteContact(pkid),
    });
  };

  const executeDeleteContact = async (pkid) => {
    setConfirmDialog({ ...confirmDialog, open: false });

    try {
      await axios.post(
        `${API_URL}delete-contact`,
        { pkid },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbarMessage('Contact deleted successfully');
      loadAdditionalContacts(formData.ID);
      loadClients(); // Refresh to update contact count badge
    } catch (error) {
      console.error("Delete contact error:", error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to delete contact');
    }
  };

  const handleCancelEditContact = () => {
    setEditingContactId(null);
    setContactFormData({ Contact: '', MobilePhone: '', Email: '' });
  };

  // Handle row expansion for viewing contacts
  const handleToggleRow = async (clientId) => {
    const newExpanded = new Set(expandedRows);
    
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
      setExpandedRows(newExpanded);
    } else {
      newExpanded.add(clientId);
      setExpandedRows(newExpanded);
      
      // Load contacts for this row if not already loaded
      if (!rowContacts[clientId]) {
        try {
          const response = await axios.get(
            `${API_URL}get-client-contacts/${clientId}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          setRowContacts(prev => ({
            ...prev,
            [clientId]: response.data.contacts || []
          }));
        } catch (error) {
          console.error("Load row contacts error:", error);
          setSnackbarMessage(error.response?.data?.error || 'Failed to load contacts');
        }
      }
    }
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Comparator function for sorting
  const descendingComparator = (a, b, orderBy) => {
    // Handle null/undefined values
    const aVal = a[orderBy] ?? '';
    const bVal = b[orderBy] ?? '';
    
    // Convert to numbers if both are numeric
    const aNum = Number(aVal);
    const bNum = Number(bVal);
    
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return bNum - aNum;
    }
    
    // String comparison
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  };

  // Load Digital Ocean projects to match with clients
  const loadDigitalOceanProjects = async () => {
    setDoProjectsLoading(true);
    try {
      const response = await axios.get(`${API_URL}digitalocean`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDoProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error loading Digital Ocean projects:', error);
      setDoProjects([]);
    } finally {
      setDoProjectsLoading(false);
    }
  };

  // Get DO asset count for a client based on matching project name
  // DO project names are formatted as: "ID | Client Name" (e.g., "9000 | Personalized Computer System")
  const getDOAssetCount = (clientId, clientName) => {
    if ((!clientId && !clientName) || doProjects.length === 0) return 0;
    
    // Convert client ID to string for matching
    const clientIdStr = clientId ? clientId.toString().trim() : '';
    
    // Find matching DO project(s) by parsing the "ID | Name" format
    const matchingProjects = doProjects.filter(project => {
      const projectName = project.name.trim();
      
      // Check if project name contains pipe separator
      if (projectName.includes('|')) {
        // Split by pipe: "9000 | Personalized Computer System" -> ["9000", "Personalized Computer System"]
        const parts = projectName.split('|').map(p => p.trim());
        const projectId = parts[0]; // "9000"
        const projectClientName = parts[1]; // "Personalized Computer System"
        
        // Match by client ID (most reliable)
        if (clientIdStr && projectId === clientIdStr) {
          return true;
        }
        
        // Fallback: match by client name
        if (clientName && projectClientName) {
          const clientNameLower = clientName.toLowerCase().trim();
          const projectNameLower = projectClientName.toLowerCase().trim();
          return projectNameLower === clientNameLower || 
                 projectNameLower.includes(clientNameLower) || 
                 clientNameLower.includes(projectNameLower);
        }
      } else {
        // Fallback for projects without pipe format
        const projectNameLower = projectName.toLowerCase();
        
        if (clientIdStr && projectNameLower.includes(clientIdStr)) {
          return true;
        }
        
        if (clientName) {
          const clientNameLower = clientName.toLowerCase().trim();
          return projectNameLower.includes(clientNameLower) || 
                 clientNameLower.includes(projectNameLower);
        }
      }
      
      return false;
    });
    
    // Count total resources across all matching projects
    const totalResources = matchingProjects.reduce((total, project) => {
      return total + (project.resources ? project.resources.length : 0);
    }, 0);
    
    return totalResources;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Filter clients based on search term across all columns including additional contacts
  const filteredClients = clients.filter(client => {
    if (!searchFilter) return true;
    
    const searchLower = searchFilter.toLowerCase();
    
    // Check main client fields
    const mainFieldsMatch = (
      (client.ID && client.ID.toString().includes(searchLower)) ||
      (client.Name && client.Name.toLowerCase().includes(searchLower)) ||
      (client.Type && client.Type.toLowerCase().includes(searchLower)) ||
      (client.Contact && client.Contact.toLowerCase().includes(searchLower)) ||
      (client.Phone && client.Phone.toLowerCase().includes(searchLower)) ||
      (client.Email && client.Email.toLowerCase().includes(searchLower)) ||
      (client.City && client.City.toLowerCase().includes(searchLower)) ||
      (client.State && client.State.toLowerCase().includes(searchLower)) ||
      (client.Address1 && client.Address1.toLowerCase().includes(searchLower)) ||
      (client.Address2 && client.Address2.toLowerCase().includes(searchLower)) ||
      (client.Zip && client.Zip.toLowerCase().includes(searchLower)) ||
      (client.last_activity_date && moment(client.last_activity_date).format('MM/DD/YYYY').includes(searchLower))
    );
    
    // Check additional contacts if present
    const contactsMatch = client.additional_contacts_data && 
      client.additional_contacts_data.toLowerCase().includes(searchLower);
    
    return mainFieldsMatch || contactsMatch;
  });

  // Apply sorting
  const sortedClients = [...filteredClients].sort(getComparator(order, orderBy));

  // Apply pagination
  const paginatedClients = sortedClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Only super_admin can access this page
  if (user.type !== 'super_admin') {
    return (
      <div className={classes.root}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1">
          You don't have permission to access this page.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">
          Manage Clients
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Client
        </Button>
      </Box>

      {isLoading && <LinearProgress />}

      <Box style={{ marginBottom: 16 }}>
        <TextField
          label="Search Clients"
          variant="outlined"
          fullWidth
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          placeholder="Search across all fields..."
          id="manage-clients-search"
          name="manageClientsSearch"
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Paper className={classes.tableContainer}>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 48 }}></TableCell>
                <TableCell sortDirection={orderBy === 'ID' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'ID'}
                    direction={orderBy === 'ID' ? order : 'asc'}
                    onClick={() => handleRequestSort('ID')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'Name' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'Name'}
                    direction={orderBy === 'Name' ? order : 'asc'}
                    onClick={() => handleRequestSort('Name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'Type' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'Type'}
                    direction={orderBy === 'Type' ? order : 'asc'}
                    onClick={() => handleRequestSort('Type')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'Contact' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'Contact'}
                    direction={orderBy === 'Contact' ? order : 'asc'}
                    onClick={() => handleRequestSort('Contact')}
                  >
                    Contact
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'Phone' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'Phone'}
                    direction={orderBy === 'Phone' ? order : 'asc'}
                    onClick={() => handleRequestSort('Phone')}
                  >
                    Phone
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'Email' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'Email'}
                    direction={orderBy === 'Email' ? order : 'asc'}
                    onClick={() => handleRequestSort('Email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'City' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'City'}
                    direction={orderBy === 'City' ? order : 'asc'}
                    onClick={() => handleRequestSort('City')}
                  >
                    City
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'State' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'State'}
                    direction={orderBy === 'State' ? order : 'asc'}
                    onClick={() => handleRequestSort('State')}
                  >
                    State
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'total_hours' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'total_hours'}
                    direction={orderBy === 'total_hours' ? order : 'asc'}
                    onClick={() => handleRequestSort('total_hours')}
                  >
                    Total Hours
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Digital Ocean Assets">
                    <span>DO Assets</span>
                  </Tooltip>
                </TableCell>
                <TableCell sortDirection={orderBy === 'last_activity_date' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'last_activity_date'}
                    direction={orderBy === 'last_activity_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('last_activity_date')}
                  >
                    Last Activity
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchFilter ? 'No clients found matching your search' : 'No clients available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedClients.map((client) => (
                  <React.Fragment key={client.ID}>
                    <TableRow>
                      <TableCell>
                        {client.additional_contacts_count > 0 ? (
                          <IconButton
                            size="small"
                            onClick={() => handleToggleRow(client.ID)}
                            className={classes.expandButton}
                          >
                            {expandedRows.has(client.ID) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        ) : (
                          <Box width={32} /> 
                        )}
                      </TableCell>
                      <TableCell>{client.ID}</TableCell>
                      <TableCell>{client.Name}</TableCell>
                      <TableCell>{client.Type}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{client.Contact}</span>
                          {client.additional_contacts_count > 0 && (
                            <Chip 
                              label={`+${client.additional_contacts_count}`} 
                              size="small" 
                              color="primary"
                              className={classes.contactChip}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{client.Phone}</TableCell>
                      <TableCell>{client.Email}</TableCell>
                      <TableCell>{client.City}</TableCell>
                      <TableCell>{client.State}</TableCell>
                      <TableCell align="right">
                        <strong>{Math.ceil(client.total_hours) || 0}</strong>
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const assetCount = getDOAssetCount(client.ID, client.Name);
                          return assetCount > 0 ? (
                            <Tooltip title={`${assetCount} Digital Ocean asset${assetCount !== 1 ? 's' : ''}`}>
                              <Chip 
                                label={assetCount} 
                                size="small" 
                                color="secondary"
                                style={{ minWidth: 32, fontWeight: 600 }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.813rem' }}>
                              —
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {client.last_activity_date ? (
                          <Box>
                            <Typography variant="body2" style={{ lineHeight: 1.1, fontSize: '0.813rem' }}>
                              {moment(client.last_activity_date).format('MM/DD/YY')}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.65rem', lineHeight: 1 }}>
                              {moment(client.last_activity_date).fromNow()}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.813rem' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit Client">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenDialog(client)}
                          className={classes.actionButton}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Client">
                        <IconButton 
                          size="small" 
                          color="secondary"
                          onClick={() => handleDeleteClient(client)}
                          className={classes.actionButton}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row for Additional Contacts */}
                  {client.additional_contacts_count > 0 && (
                    <TableRow>
                      <TableCell colSpan={13} style={{ padding: 0, borderBottom: 'none' }}>
                        <Collapse in={expandedRows.has(client.ID)} timeout="auto" unmountOnExit>
                          <Box className={classes.expandedContent}>
                            <Typography variant="subtitle2" gutterBottom style={{ fontSize: '0.813rem', marginBottom: 8 }}>
                              Additional Contacts ({client.additional_contacts_count})
                            </Typography>
                            {rowContacts[client.ID] ? (
                              <Box className={classes.contactsList}>
                                {rowContacts[client.ID].map((contact) => (
                                  <Box key={contact.pkid} className={classes.contactItem}>
                                    <Box flex={1}>
                                      <Typography variant="body2" style={{ fontSize: '0.813rem' }}>
                                        <strong>{contact.Contact}</strong>
                                      </Typography>
                                    </Box>
                                    <Box flex={1}>
                                      <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                                        Mobile
                                      </Typography>
                                      <Typography variant="body2" style={{ fontSize: '0.813rem' }}>
                                        {contact.MobilePhone || '—'}
                                      </Typography>
                                    </Box>
                                    <Box flex={1}>
                                      <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                                        Email
                                      </Typography>
                                      <Typography variant="body2" style={{ fontSize: '0.813rem' }}>
                                        {contact.Email || '—'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Box textAlign="center" py={2}>
                                <Typography variant="body2" color="textSecondary">
                                  Loading contacts...
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredClients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Edit Client Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth 
        disableEnforceFocus={true}>
        <DialogTitle>
          {editMode ? 'Edit Client' : 'Add New Client'}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <TextField
                  label="Client ID"
                  value={formData.ID || ''}
                  onChange={(e) => handleInputChange('ID', e.target.value)}
                  fullWidth
                  disabled={editMode}
                  required
                  type="number"
                  id="client-id"
                  name="clientId"
                  helperText={
                    editMode 
                      ? "ID cannot be changed" 
                      : idCheckMessage || "Suggested next ID - can be modified if needed"
                  }
                  error={!idAvailable && !editMode}
                />
                {!editMode && (
                  <Tooltip title="Get next available ID">
                    <IconButton 
                      color="primary" 
                      onClick={fetchNextClientId}
                      style={{ marginTop: 8 }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Client Name"
                value={formData.Name}
                onChange={(e) => handleInputChange('Name', e.target.value)}
                fullWidth
                required
                id="client-name"
                name="clientName"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Type"
                value={formData.Type || ''}
                onChange={(e) => handleInputChange('Type', e.target.value)}
                fullWidth
                helperText="Select client type"
                id="client-type"
                name="clientType"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="HOME">HOME</MenuItem>
                <MenuItem value="CORPORATE">CORPORATE</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Member Since"
                type="date"
                value={formData.membersince}
                onChange={(e) => handleInputChange('membersince', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                id="client-member-since"
                name="memberSince"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address Line 1"
                value={formData.Address1}
                onChange={(e) => handleInputChange('Address1', e.target.value)}
                fullWidth
                id="client-address1"
                name="address1"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address Line 2"
                value={formData.Address2}
                onChange={(e) => handleInputChange('Address2', e.target.value)}
                fullWidth
                id="client-address2"
                name="address2"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                value={formData.City}
                onChange={(e) => handleInputChange('City', e.target.value)}
                fullWidth
                id="client-city"
                name="city"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="State"
                value={formData.State}
                onChange={(e) => handleInputChange('State', e.target.value)}
                fullWidth
                id="client-state"
                name="state"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Zip"
                value={formData.Zip}
                onChange={(e) => handleInputChange('Zip', e.target.value)}
                fullWidth
                id="client-zip"
                name="zip"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Person"
                value={formData.Contact}
                onChange={(e) => handleInputChange('Contact', e.target.value)}
                fullWidth
                id="client-contact"
                name="contact"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={formData.Phone}
                onChange={(e) => handleInputChange('Phone', e.target.value)}
                fullWidth
                id="client-phone"
                name="phone"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                value={formData.Email}
                onChange={(e) => handleInputChange('Email', e.target.value)}
                fullWidth
                id="client-email"
                name="email"
              />
            </Grid>

            {/* Additional Contacts Section */}
            {editMode && formData.ID && (
              <Grid item xs={12}>
                <Box className={classes.contactsSection}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Additional Contacts
                    </Typography>
                    <Chip 
                      label={`${additionalContacts.length} contacts`} 
                      size="small" 
                      color="primary"
                    />
                  </Box>
                  
                  <Divider style={{ marginBottom: 16 }} />

                  {/* Contact Form */}
                  <Card variant="outlined" style={{ marginBottom: 16 }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {editingContactId ? 'Edit Contact' : 'Add New Contact'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Contact Name *"
                            value={contactFormData.Contact}
                            onChange={(e) => setContactFormData({...contactFormData, Contact: e.target.value})}
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 20 }}
                            id="additional-contact-name"
                            name="additionalContactName"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Mobile Phone"
                            value={contactFormData.MobilePhone}
                            onChange={(e) => setContactFormData({...contactFormData, MobilePhone: e.target.value})}
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 13 }}
                            id="additional-contact-phone"
                            name="additionalContactPhone"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Email"
                            type="email"
                            value={contactFormData.Email}
                            onChange={(e) => setContactFormData({...contactFormData, Email: e.target.value})}
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 30 }}
                            id="additional-contact-email"
                            name="additionalContactEmail"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            {editingContactId && (
                              <Button 
                                size="small" 
                                onClick={handleCancelEditContact}
                              >
                                Cancel
                              </Button>
                            )}
                            <Button 
                              variant="contained" 
                              color="primary" 
                              size="small"
                              onClick={editingContactId ? handleUpdateContact : handleAddContact}
                              disabled={!contactFormData.Contact}
                            >
                              {editingContactId ? 'Update Contact' : 'Add Contact'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Contacts List */}
                  {additionalContacts.length > 0 ? (
                    <Box>
                      {additionalContacts.map((contact) => (
                        <Card key={contact.pkid} className={classes.contactCard} variant="outlined">
                          <CardContent className={classes.contactCardContent}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                                  Name
                                </Typography>
                                <Typography variant="body1">
                                  <strong>{contact.Contact}</strong>
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                                  Mobile
                                </Typography>
                                <Typography variant="body2">
                                  {contact.MobilePhone || '—'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                                  Email
                                </Typography>
                                <Typography variant="body2" style={{ wordBreak: 'break-word' }}>
                                  {contact.Email || '—'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <Box display="flex" justifyContent="flex-end" gap={1}>
                                  <Tooltip title="Edit">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleEditContact(contact)}
                                      disabled={editingContactId !== null}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton 
                                      size="small" 
                                      color="secondary"
                                      onClick={() => handleDeleteContact(contact.pkid)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Box textAlign="center" py={2}>
                      <Typography variant="body2" color="textSecondary">
                        No additional contacts yet. Add one above.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveClient} 
            color="primary" 
            variant="contained"
            disabled={isLoading || (!editMode && !idAvailable)}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" color="error.main">
            <DeleteIcon style={{ marginRight: 8 }} />
            <Typography variant="h6" component="span">
              Confirm Client Deletion
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {clientToDelete && deletionImpact && (
            <Box>
              <Typography variant="body1" gutterBottom>
                You are about to permanently delete:
              </Typography>
              <Box bgcolor="error.light" p={2} my={2} borderRadius={1}>
                <Typography variant="h6" color="error.dark">
                  {clientToDelete.Name} (ID: {clientToDelete.ID})
                </Typography>
              </Box>

              {/* Impact Summary */}
              <Box bgcolor="warning.light" p={2} my={2} borderRadius={1}>
                <Typography variant="subtitle2" gutterBottom color="warning.dark">
                  ⚠️ Deletion Impact:
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {deletionImpact.hours_records > 0 && (
                    <li>
                      <Typography variant="body2">
                        <strong>{deletionImpact.hours_records}</strong> hours records 
                        ({deletionImpact.total_hours} total hours) will remain in the system 
                        but will be orphaned
                      </Typography>
                    </li>
                  )}
                  {deletionImpact.assigned_users > 0 && (
                    <li>
                      <Typography variant="body2">
                        <strong>{deletionImpact.assigned_users}</strong> user(s) are assigned to this client
                      </Typography>
                    </li>
                  )}
                  {deletionImpact.additional_contacts > 0 && (
                    <li>
                      <Typography variant="body2">
                        <strong>{deletionImpact.additional_contacts}</strong> additional contact(s) will be deleted
                      </Typography>
                    </li>
                  )}
                  {deletionImpact.last_activity && (
                    <li>
                      <Typography variant="body2">
                        Last activity: <strong>{moment(deletionImpact.last_activity).format('MM/DD/YYYY')}</strong> 
                        ({moment(deletionImpact.last_activity).fromNow()})
                      </Typography>
                    </li>
                  )}
                </ul>
              </Box>

              <Box my={3}>
                <Typography variant="body1" gutterBottom>
                  <strong>This action cannot be undone.</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  To confirm deletion, please type the client name exactly as shown above:
                </Typography>
                <TextField
                  fullWidth
                  margin="normal"
                  placeholder={`Type "${clientToDelete.Name}" to confirm`}
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  variant="outlined"
                  autoComplete="off"
                  id="delete-confirmation-input"
                  name="deleteConfirmation"
                  error={deleteConfirmation.length > 0 && deleteConfirmation !== clientToDelete.Name}
                  helperText={
                    deleteConfirmation.length > 0 && deleteConfirmation !== clientToDelete.Name
                      ? "Name doesn't match"
                      : ""
                  }
                />
              </Box>

              {deletionImpact.hours_records === 0 && deletionImpact.assigned_users === 0 && deletionImpact.additional_contacts === 0 && (
                <Box bgcolor="success.light" p={1} borderRadius={1}>
                  <Typography variant="body2" color="success.dark">
                    ✓ This client has no associated hours, assigned users, or contacts. Safe to delete.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            color="secondary"
            variant="contained"
            disabled={isLoading || deleteConfirmation !== clientToDelete?.Name}
            startIcon={<DeleteIcon />}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        confirmText="Delete"
        confirmColor="secondary"
      />
    </div>
  );
};

export default ManageClients;
